import React, { useEffect } from 'react';
import { Table, Button, Space, message, Modal, Spin } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { fetchEmployees, deleteEmployee } from '../redux/slices/employeeSlice';
import { EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { canManageEmployees } from '../utils/permissions';

const EmployeeTable = ({ onEdit, onView, filters = {} }) => {
  const dispatch = useDispatch();
  const { employees, pagination, isLoading } = useSelector(state => state.employees);
  const { user } = useSelector((state) => state.auth);

  useEffect(() => {
    dispatch(fetchEmployees({ page: 1, limit: 10, filters }));
  }, [dispatch, filters]);

  const handleDelete = (id) => {
    Modal.confirm({
      title: 'Delete Employee',
      content: 'Are you sure you want to delete this employee?',
      okText: 'Yes',
      cancelText: 'No',
      async onOk() {
        try {
          await dispatch(deleteEmployee(id)).unwrap();
          message.success('Employee deleted successfully');
        } catch (err) {
          message.error(err || 'Failed to delete employee');
        }
      }
    });
  };

  const columns = [
    {
      title: 'Employee Code',
      dataIndex: 'employeeCode',
      key: 'employeeCode',
    },
    {
      title: 'Name',
      dataIndex: 'firstName',
      key: 'name',
      render: (text, record) => `${record.firstName} ${record.lastName}`
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Designation',
      dataIndex: 'designation',
      key: 'designation',
    },
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
      render: (_, record) => record.department?.name || '-'
    },
    {
      title: 'Manager',
      dataIndex: 'manager',
      key: 'manager',
      render: (_, record) => (
        record.manager ? `${record.manager.firstName} ${record.manager.lastName}` : '-'
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <span style={{ color: status === 'Active' ? 'green' : 'red' }}>
          {status}
        </span>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            type="link"
            onClick={() => onView(record.id)}
          />
          {canManageEmployees(user) && (
            <Button
              icon={<EditOutlined />}
              type="link"
              onClick={() => onEdit(record.id)}
            />
          )}
          {canManageEmployees(user) && (
            <Button
              icon={<DeleteOutlined />}
              type="link"
              danger
              onClick={() => handleDelete(record.id)}
            />
          )}
        </Space>
      )
    }
  ];

  const handleTableChange = (pagination) => {
    dispatch(fetchEmployees({
      page: pagination.current,
      limit: pagination.pageSize,
      filters
    }));
  };

  return (
    <Spin spinning={isLoading}>
      <Table
        columns={columns}
        dataSource={employees}
        pagination={{
          current: pagination.page,
          pageSize: pagination.limit,
          total: pagination.total,
          showSizeChanger: true
        }}
        onChange={handleTableChange}
        rowKey="id"
      />
    </Spin>
  );
};

export default EmployeeTable;
